import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MessageSquare, ChevronDown, ChevronUp, Archive, Trash2 } from 'lucide-react';
import FeedbackThread from '../../components/feedback/FeedbackThread';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFeedback = ({ feedbackItems, fetchData }) => {
  const [feedbackFilter, setFeedbackFilter] = useState({ status: '', category: '', sentiment: '' });
  const [expandedId, setExpandedId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const filteredFeedback = feedbackItems.filter(fb => {
    if (feedbackFilter.status && fb.status !== feedbackFilter.status) return false;
    if (feedbackFilter.category && fb.category !== feedbackFilter.category) return false;
    if (feedbackFilter.sentiment && fb.sentiment !== feedbackFilter.sentiment) return false;
    return true;
  });
  const openFeedback = feedbackItems.filter(fb => fb.status === 'open').length;

  const handleFeedbackStatus = async (fbId, status) => {
    try {
      await axios.put(`${API_URL}/api/feedback/${fbId}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      fetchData();
    } catch (err) { toast.error('Failed to update status'); }
  };

  const handleArchive = async (fbId) => {
    try {
      await axios.put(`${API_URL}/api/feedback/${fbId}/archive`);
      toast.success('Feedback archived');
      fetchData();
    } catch (err) { toast.error('Failed to archive'); }
  };

  const handleDelete = async (fbId) => {
    if (!window.confirm('Permanently delete this feedback? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/feedback/${fbId}`);
      toast.success('Feedback deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleExpand = async (fb) => {
    const next = expandedId === fb.id ? null : fb.id;
    setExpandedId(next);
    // Mark admin-side as read when opening an item with unread user replies
    if (next && fb.unread_for_me) {
      try {
        await axios.put(`${API_URL}/api/feedback/${fb.id}/mark-read`);
        fetchData();
      } catch (err) {
        console.error('Failed to mark feedback read:', err);
      }
    }
  };

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="feedback-section">
      <div className="p-4 sm:p-6 cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Feedback</h2>
            {openFeedback > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">{openFeedback} open</span>
            )}
          </div>
          {collapsed ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronUp className="h-4 w-4 text-slate-400" />}
        </div>
      </div>

      {!collapsed && (
      <CardContent className="pt-0 px-4 sm:px-6 pb-4 sm:pb-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          <select
            value={feedbackFilter.status}
            onChange={e => setFeedbackFilter({ ...feedbackFilter, status: e.target.value })}
            className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1"
            data-testid="filter-status"
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={feedbackFilter.category}
            onChange={e => setFeedbackFilter({ ...feedbackFilter, category: e.target.value })}
            className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1"
            data-testid="filter-category"
          >
            <option value="">All categories</option>
            <option value="bug_report">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="general">General</option>
          </select>
          <select
            value={feedbackFilter.sentiment}
            onChange={e => setFeedbackFilter({ ...feedbackFilter, sentiment: e.target.value })}
            className="bg-slate-900 border border-slate-600 text-white text-xs rounded px-2 py-1"
            data-testid="filter-sentiment"
          >
            <option value="">All sentiments</option>
            <option value="positive">Positive</option>
            <option value="neutral">Neutral</option>
            <option value="negative">Negative</option>
          </select>
        </div>

        {filteredFeedback.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">No feedback matches filters.</p>
        ) : (
          <div className="space-y-2">
            {filteredFeedback.map(fb => {
              const isExpanded = expandedId === fb.id;
              return (
                <div
                  key={fb.id}
                  className={`rounded-lg border transition-all ${
                    fb.unread_for_me ? 'border-amber-500/40 bg-amber-500/10' :
                    fb.status === 'resolved' ? 'border-emerald-500/20 bg-emerald-500/5' :
                    fb.status === 'archived' ? 'border-slate-700 bg-slate-900/20 opacity-70' :
                    'border-slate-700 bg-slate-900/40'
                  }`}
                  data-testid={`admin-feedback-${fb.id}`}
                >
                  <div className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-medium text-white">{fb.user_name || fb.user_email}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            fb.category === 'bug_report' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                            fb.category === 'feature_request' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' :
                            'text-slate-400 bg-slate-500/10 border-slate-500/30'
                          }`}>{fb.category?.replace('_', ' ')}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            fb.sentiment === 'positive' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' :
                            fb.sentiment === 'negative' ? 'text-red-400 bg-red-500/10 border-red-500/30' :
                            'text-slate-400 bg-slate-500/10 border-slate-500/30'
                          }`}>{fb.sentiment}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            fb.status === 'archived' ? 'text-slate-500 bg-slate-500/10 border-slate-600/30' :
                            fb.status === 'resolved' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' :
                            fb.status === 'reviewed' ? 'text-blue-400 bg-blue-500/10 border-blue-500/30' :
                            'text-amber-400 bg-amber-500/10 border-amber-500/30'
                          }`}>{fb.status}</span>
                          {fb.unread_for_me && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-amber-400 bg-amber-500/15 border-amber-500/40 font-medium animate-pulse">
                              New reply
                            </span>
                          )}
                          {fb.thread && fb.thread.length > 1 && !fb.unread_for_me && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-slate-400 bg-slate-700/40 border-slate-600/40">
                              {fb.thread.length} messages
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-300 whitespace-pre-wrap line-clamp-2">{fb.message}</p>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {fb.page && <span>on {fb.page} — </span>}
                          {new Date(fb.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm"
                          onClick={() => handleExpand(fb)}
                          className="text-slate-400 hover:text-amber-400 h-7 text-xs"
                          data-testid={`conversation-btn-${fb.id}`}>
                          {isExpanded ? 'Hide' : 'Open'} conversation
                        </Button>
                        {fb.status !== 'resolved' && fb.status !== 'archived' && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleFeedbackStatus(fb.id, 'resolved')}
                            className="text-slate-400 hover:text-emerald-400 h-7 text-xs" data-testid={`resolve-btn-${fb.id}`}>
                            Resolve
                          </Button>
                        )}
                        {fb.status !== 'archived' && (
                          <Button variant="ghost" size="sm"
                            onClick={() => handleArchive(fb.id)}
                            className="text-slate-400 hover:text-sky-400 h-7 text-xs" data-testid={`archive-btn-${fb.id}`}>
                            <Archive className="h-3 w-3 mr-1" />Archive
                          </Button>
                        )}
                        <Button variant="ghost" size="sm"
                          onClick={() => handleDelete(fb.id)}
                          className="text-slate-400 hover:text-red-400 h-7 text-xs" data-testid={`delete-btn-${fb.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" />Delete
                        </Button>
                      </div>
                    </div>

                    {/* Thread */}
                    {isExpanded && (
                      <div className="mt-3 border-t border-slate-700/50 pt-3">
                        {fb.client_meta && (
                          <div className="mb-3 p-2 rounded-md bg-slate-900/60 border border-slate-700/40 text-[10px] font-mono text-slate-500 leading-relaxed" data-testid={`triage-${fb.id}`}>
                            <div className="text-slate-400 font-sans font-medium mb-1 not-italic">Triage info</div>
                            {fb.client_meta.fe_build && <div>FE built: {new Date(fb.client_meta.fe_build).toLocaleString('en-GB')}</div>}
                            {fb.client_meta.be_deployed && <div>BE deployed: {new Date(fb.client_meta.be_deployed).toLocaleString('en-GB')}</div>}
                            {fb.client_meta.viewport && <div>Viewport: {fb.client_meta.viewport}</div>}
                            {fb.client_meta.ua && <div className="break-all">UA: {fb.client_meta.ua}</div>}
                          </div>
                        )}
                        <FeedbackThread
                          feedbackId={fb.id}
                          thread={fb.thread || []}
                          viewerRole="admin"
                          onMessageSent={fetchData}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
};

export default AdminFeedback;
